import { usePermissionGate } from '@/components/providers/PermissionsContextProvider/usePermissionGate';
import { Feature } from '@/components/shared/constants/features';
import { Button } from 'primereact/button';
import { Column, ColumnSortEvent } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useEffect, useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import {
    COLUMN_WIDTH_PERCENT,
    DisabledButtonTitleWrap,
    HIGHLIGHT_ROW_CLASS,
    HighlightableRows,
    LabelWrapper,
    getLabelWidthPercent,
    PencilButton,
    toWidth,
} from './TemplateManager.StyledComponents';
import { BaseTemplate, TemplateExtraColumn, TemplateManagerConfig } from './TemplateManager.types';
import { sortByColumn } from './sortTemplates';

const MANAGE_DENIED_MESSAGE = "You don't have permission to manage templates.";
const EDIT_DENIED_MESSAGE = "You don't have permission to edit templates.";
const RETIRE_DENIED_MESSAGE = 'System templates cannot be retired.';

interface TemplateTableProps<T extends BaseTemplate> {
    items: T[];
    canManage: boolean;
    canEdit: boolean;
    extraBadge?: TemplateManagerConfig<T>['extraBadge'];
    extraColumns?: TemplateManagerConfig<T>['extraColumns'];
    canRetire?: TemplateManagerConfig<T>['canRetire'];
    /** The row to scroll to and ring, e.g. the template just created. */
    highlightedId?: string | null;
    onEdit: (item: T) => void;
    onRetire: (item: T) => Promise<T>;
    onReactivate: (item: T) => Promise<T>;
}

interface PendingAction<T> {
    item: T;
    kind: 'retire' | 'reactivate';
}

export const TemplateTable = <T extends BaseTemplate>({
    items,
    canManage,
    canEdit,
    extraBadge,
    extraColumns = [],
    canRetire,
    highlightedId = null,
    onEdit,
    onRetire,
    onReactivate,
}: TemplateTableProps<T>) => {
    const manageGate = usePermissionGate(Feature.ManageTemplates, MANAGE_DENIED_MESSAGE, canManage);
    const editGate = usePermissionGate(Feature.EditTemplates, EDIT_DENIED_MESSAGE, canEdit);
    const [pendingAction, setPendingAction] = useState<PendingAction<T> | null>(null);

    // Re-runs when items change so a row that arrives after the refetch is still found.
    useEffect(() => {
        if (highlightedId == null) {
            return;
        }
        document
            .querySelector(`[data-template-id="${highlightedId}"]`)
            ?.closest('tr')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [highlightedId, items]);

    // PrimeReact sorts the value itself and calls this, so each column decides what its text is.
    const sortableBy = (field: string, getValue: (item: T) => string | null) => ({
        sortable: true,
        sortField: field,
        sortFunction: (event: ColumnSortEvent) => sortByColumn(event.data, getValue, event.order),
    });

    const extraSortProps = (column: TemplateExtraColumn<T>) =>
        column.sortValue ? sortableBy(column.header, column.sortValue) : {};

    const labelWidth = toWidth(
        getLabelWidthPercent(extraColumns.map((column) => column.widthPercent)),
    );

    const labelBody = (item: T) => (
        <LabelWrapper data-template-id={item.id}>
            <PencilButton
                type="button"
                aria-label={`Edit ${item.label}`}
                data-testid={`edit-template-pencil-${item.id}`}
                aria-disabled={editGate.disabled || undefined}
                title={editGate.title ?? 'Edit'}
                onClick={editGate.gateAction(() => onEdit(item))}
            >
                <i className="pi pi-pencil" aria-hidden="true" />
            </PencilButton>
            {item.label}
            {extraBadge?.(item)}
        </LabelWrapper>
    );

    const activeBody = (item: T) => (item.isActive ? 'Active' : 'Retired');

    const actionsBody = (item: T) => {
        const editButton = (
            <Button
                label="Edit"
                text
                data-testid={`edit-template-${item.id}`}
                aria-disabled={editGate.disabled || undefined}
                title={editGate.title}
                onClick={editGate.gateAction(() => onEdit(item))}
            />
        );

        if (!item.isActive) {
            return (
                <>
                    {editButton}
                    <Button
                        label="Reactivate"
                        text
                        severity="success"
                        data-testid={`reactivate-template-${item.id}`}
                        aria-disabled={manageGate.disabled || undefined}
                        title={manageGate.title}
                        onClick={manageGate.gateAction(() => setPendingAction({ item, kind: 'reactivate' }))}
                    />
                </>
            );
        }

        // A real `disabled` (not just aria-disabled) so templates the domain marks unretirable (e.g. coded or
        // triggered conditions) can't be clicked at all — a rule about the item, not a permission the user lacks. Browsers don't show a native
        // `title` tooltip on a disabled element, so the explanation moves to a wrapping span.
        const retireAllowed = canRetire ? canRetire(item) : true;
        const retireButton = (
            <Button
                label="Retire"
                text
                severity="danger"
                data-testid={`retire-template-${item.id}`}
                disabled={!retireAllowed}
                aria-disabled={manageGate.disabled || undefined}
                style={retireAllowed ? undefined : { cursor: 'not-allowed' }}
                title={retireAllowed ? manageGate.title : undefined}
                onClick={
                    retireAllowed
                        ? manageGate.gateAction(() => setPendingAction({ item, kind: 'retire' }))
                        : undefined
                }
            />
        );

        return (
            <>
                {editButton}
                {retireAllowed ? (
                    retireButton
                ) : (
                    <DisabledButtonTitleWrap title={RETIRE_DENIED_MESSAGE}>
                        {retireButton}
                    </DisabledButtonTitleWrap>
                )}
            </>
        );
    };

    return (
        <>
            <HighlightableRows>
                <DataTable
                    value={items}
                    dataKey="id"
                    stripedRows
                    removableSort
                    rowClassName={(item: T) =>
                        item.id === highlightedId ? HIGHLIGHT_ROW_CLASS : ''
                    }
                    data-testid="template-table"
                >
                    <Column
                        header="Label"
                        body={labelBody}
                        style={{ width: labelWidth }}
                        {...sortableBy('label', (item) => item.label)}
                    />
                    {extraColumns.map((column) => (
                        <Column
                            key={column.header}
                            header={column.header}
                            body={column.body}
                            style={{ width: toWidth(column.widthPercent) }}
                            {...extraSortProps(column)}
                        />
                    ))}
                    <Column
                        header="Status"
                        body={activeBody}
                        style={{ width: toWidth(COLUMN_WIDTH_PERCENT.status) }}
                        {...sortableBy('status', activeBody)}
                    />
                    <Column
                        header=""
                        body={actionsBody}
                        style={{ width: toWidth(COLUMN_WIDTH_PERCENT.actions) }}
                    />
                </DataTable>
            </HighlightableRows>
            {pendingAction ? (
                <ConfirmDialog
                    visible
                    title={pendingAction.kind === 'retire' ? 'Retire template?' : 'Reactivate template?'}
                    message={
                        pendingAction.kind === 'retire'
                            ? `"${pendingAction.item.label}" will be hidden from new use until it's reactivated.`
                            : `"${pendingAction.item.label}" will become available for use again.`
                    }
                    confirmLabel={pendingAction.kind === 'retire' ? 'Retire' : 'Reactivate'}
                    confirmSeverity={pendingAction.kind === 'retire' ? 'danger' : 'success'}
                    onConfirm={async () => {
                        if (pendingAction.kind === 'retire') {
                            await onRetire(pendingAction.item);
                        } else {
                            await onReactivate(pendingAction.item);
                        }
                        setPendingAction(null);
                    }}
                    onCancel={() => setPendingAction(null)}
                />
            ) : null}
        </>
    );
};
